import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useSuiteAPI } from 'src/SharedCustomHooks/UseSuiteAPI/useSuiteAPI';
import {
  resolveFloorPlanUrl,
  mapStallEntries,
  stripOtherAreaIds,
  buildSuiteCreateParameters,
  buildSuiteUpdateParameters,
} from 'src/SharedCustomHooks/UseSaveSuite/helpers';

export interface SaveSuiteResult {
  ok: boolean;
  suite: any | null;
}

export interface UseSaveSuiteReturn {
  saveSuite: (
    formData: any,
    suiteAreas: any[],
    floorImage: any,
    createPropertyId?: any
  ) => Promise<SaveSuiteResult>;
  isSaving: boolean;
}

export const useSaveSuite = (): UseSaveSuiteReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const { createSuite, updateSuite, isCreatingSuite, isUpdatingSuite } =
    useSuiteAPI();

  const saveSuite = async (
    formData: any,
    suiteAreas: any[],
    floorImage: any,
    createPropertyId?: any
  ): Promise<SaveSuiteResult> => {
    const imageUrl: any = await resolveFloorPlanUrl(
      formData.floorPlanURL,
      floorImage
    );
    const isNewImage: boolean =
      floorImage !== formData.floorPlanURL && formData.floorPlanURL?.length > 0;

    const areas: any[] = stripOtherAreaIds(suiteAreas);

    const parkingStallsArray: any[] = mapStallEntries(
      formData.parkingStalls,
      'ParkingStalls'
    );

    const storageLockerArray: any[] = mapStallEntries(
      formData.storageLockers,
      'StorageLockers'
    );

    if (formData.id) {
      const params = buildSuiteUpdateParameters({
        suiteData: formData,
        userId: user.id,
        imageUrl,
        isNewImage,
        parkingStallsArray,
        storageLockerArray,
        areas,
      });

      const { ok, suite } = await updateSuite(params);

      return { ok, suite };
    }

    const params = buildSuiteCreateParameters({
      suiteData: formData,
      userId: user.id,
      propertyId: createPropertyId ?? formData.propertyId,
      imageUrl,
      isNewImage,
      parkingStallsArray,
      storageLockerArray,
      areas,
    });

    const { ok, suite } = await createSuite(params);

    return { ok, suite };
  };

  return { saveSuite, isSaving: isCreatingSuite || isUpdatingSuite };
};
